import { APP_CONFIG } from "../../init";
import { APIClient } from "../../utilities/APIClient";
import { DateTime } from "luxon";
import { contentPageResponse } from "../commonTypes";
import { Video, videoDTO, VideoLibrary } from "./Video";
import { handleErrors } from "../../app/APIError";
import { dirbatLocale } from "../../translations/translations";

function getVideo(videoData: []): Video[] {
  return videoData.map((nd: any) => mapVideoFromApi(nd));
}

function mapVideoFromApi(vd: any): Video {
  return {
    headline: vd.headline,
    id: vd.id,
    triggersNotification: vd.triggersNotification,
    photo: vd.imageUrl,
    publicationDate: DateTime.fromISO(vd.publishedAt),
    removalDate: DateTime.fromISO(vd.unpublishedAt),
    status: vd.status,
    title: vd.title,
    url: vd.url,
  };
}

export class VideoLibraryAPI extends APIClient implements VideoLibrary {
  constructor(
    token: string,
    clientId: string,
    private supplierId: string,
    language: dirbatLocale
  ) {
    super(token, language);
  }
  findPaginated(
    firstIdx: number,
    itemsPerPage: number
  ): Promise<contentPageResponse<Video>> {
    return this.fetch(
      `${APP_CONFIG.API_BASE_URI}/suppliers/${this.supplierId}/videos?page=${
        Math.floor(firstIdx / itemsPerPage) + 1
      }`
    )
      .then((response) => response.json())
      .then((videoData) => {
        return {
          items: getVideo(videoData["hydra:member"]),
          stats: { totalCount: videoData["hydra:totalItems"] },
        };
      });
  }

  findOneById(id: string): Promise<Video> {
    return this.fetch(
      `${APP_CONFIG.API_BASE_URI}/suppliers/${this.supplierId}/videos/${id}`
    )
      .then((response) => response.json())
      .then((videoData): Video => mapVideoFromApi(videoData));
  }

  delete(videos: Video): Promise<boolean> {
    return this.fetch(
      `${APP_CONFIG.API_BASE_URI}/suppliers/${this.supplierId}/videos/${videos.id}`,
      {
        method: "delete",
      }
    )
      .then((response) => {
        return response.status === 204;
      })
      .then((success): boolean => success);
  }

  save(dto: videoDTO): Promise<Video> {
    const method = dto.image ? "POST" : "PATCH";
    const endPointUrl = dto.id
      ? `${APP_CONFIG.API_BASE_URI}/suppliers/${this.supplierId}/videos/${dto.id}`
      : `${APP_CONFIG.API_BASE_URI}/suppliers/${this.supplierId}/videos`;
    let contentType;
    let body;
    if (dto.image === undefined) {
      contentType = "application/merge-patch+json";
      body = JSON.stringify({
        url: dto.url,
        title: dto.title,
        headline: dto.headline,
        triggersNotification: dto.triggersNotification,
        publishedAt: dto.publicationDate?.toISO(),
        unpublishedAt: dto.removalDate?.toISO(),
        status: dto.status,
      });
    } else {
      contentType = "application/x-www-form-urlencoded";
      body = new FormData();
      body.append("url", dto.url);
      body.append("imageFile", dto.image);
      body.append("headline", dto.headline);
      body.append(
        "triggersNotification",
        dto.triggersNotification ? "true" : ""
      );
      body.append("publishedAt", dto.publicationDate!.toISO());
      body.append("title", dto.title);
      body.append("status", dto.status);
      dto.removalDate && body.append("unpublishedAt", dto.removalDate.toISO());
    }

    let init: RequestInit = {
      method: method,
      body: body,
    };
    dto.image === undefined
      ? (init.headers = {
          "Content-Type": contentType,
        })
      : false;
    return this.fetch(endPointUrl, init)
      .then(handleErrors)
      .then((videoData) => {
        return mapVideoFromApi(videoData);
      })
      .catch((e: Error) =>
        Promise.reject(JSON.parse(e.message)["hydra:description"])
      );
  }
}
